import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Container,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownItem,
  DropdownMenu,
  Row,
  Card,
  CardHeader,
  Col,
} from "reactstrap";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import DeleteModal from "../../Components/Common/DeleteModal";
import TableContainer from "../../Components/Common/TableContainer";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { getProductsQuery, getCategoriesQuery } from "../../queries";
import { deleteProduct } from "../../api/products";
import { useTranslation } from "react-i18next";
import Loader from "../../Components/Common/Loader";
import settings from "../../settings/settings";

const Products = (props) => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const currentLang = i18n.language === "rs" ? "ru" : i18n.language;

  const [productList, setProductList] = useState([]);
  const [product, setProduct] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteModalMulti, setDeleteModalMulti] = useState(false);
  const [filters, setFilters] = useState({
    productName: '',
    page: 1,
    size: 10,
    order: "asc"
  });

  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setFilters(prev => ({ ...prev, page: 1, productName: searchTerm }));
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const { data: categoriesData } = useQuery({
    ...getCategoriesQuery({ isAll: true }),
  });

  const categories = useMemo(() => {
    return categoriesData?.data?.data || [];
  }, [categoriesData]);

  const setPage = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const { data: productsData, isLoading, isError } = useQuery({
    ...getProductsQuery(filters),
    keepPreviousData: true,
  });

  useEffect(() => {
    if (productsData?.data?.data) {
      setProductList(productsData.data.data);
    }
  }, [productsData]);


  //delete order
  const onClickDelete = (product) => {
    setProduct(product);
    setDeleteModal(true);
  };

  const handleDeleteProduct = () => {
    if (product) {
      deleteProduct(product.id)
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ["products"] });
          setDeleteModal(false);
          setProduct(null);
          toast.success(t("Product deleted successfully"));
        })
        .catch(err => {
          toast.error(t("Error deleting product"));
        });
    }
  };

  const deleteMultiple = () => {
    // Implement multiple delete if API supports batch delete or Promise.all
    // Currently the API spec does not explicitly show batch delete
    const ele = document.querySelectorAll(".productCheckBox:checked");
    if (ele.length > 0) {
      Promise.all(Array.from(ele).map(el => deleteProduct(el.value)))
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ["products"] });
          setDeleteModalMulti(false);
          toast.success(t("Products deleted successfully"));
        })
        .catch(() => {
          toast.error(t("Error deleting one or more products"));
        });
    }
  };

  const columns = useMemo(() => [

    {
      header: t("Product"),
      accessorKey: "normalizedName",
      enableColumnFilter: false,
      cell: (cell) => (
        <>
          <div className="d-flex align-items-center">
            <div className="flex-shrink-0 me-3">
              <div className="avatar-sm bg-light rounded p-1">
                <img
                  src={cell.row.original.imageId ? `${settings.catalogBaseURL}/products/images/${cell.row.original.imageId}` : "https://upload.wikimedia.org/wikipedia/commons/1/14/No_Image_Available.jpg"}
                  alt=""
                  className="img-fluid d-block"
                />
              </div>
            </div>
            <div className="flex-grow-1">
              <h5 className="fs-14 mb-1">
                <Link
                  to={`/products/${cell.row.original.id}`}
                  className="text-body"
                >
                  {" "}
                  {cell.row.original.locales?.find(l => l.localeKey === currentLang)?.name || cell.row.original.locales?.[0]?.name || cell.getValue()}
                </Link>
              </h5>
              <p className="text-muted mb-0">
                {t("Code :")}{" "}
                <span className="fw-medium">
                  {" "}
                  {cell.row.original.productCode}
                </span>
              </p>
            </div>
          </div>
        </>
      ),
    },
    {
      header: t("Category"),
      accessorKey: "categoryId",
      enableColumnFilter: false,
      cell: (cell) => {
        const category = categories.find(c => c.id === cell.getValue());
        return category?.locales?.find(l => l.localeKey === currentLang)?.name || category?.locales?.[0]?.name || t("Unknown");
      },
    },
    {
      header: t("Barcode"),
      accessorKey: "barcode",
      enableColumnFilter: false,
    },
    {
      header: t("Action"),
      cell: (cell) => {
        return (
          <UncontrolledDropdown>
            <DropdownToggle
              href="#"
              className="btn btn-soft-secondary btn-sm"
              tag="button"
            >
              <i className="ri-more-fill" />
            </DropdownToggle>
            <DropdownMenu className="dropdown-menu-end">
              <DropdownItem tag={Link} to={`/products/${cell.row.original.id}`}>
                <i className="ri-eye-fill align-bottom me-2 text-muted"></i>{" "}
                {t("View")}
              </DropdownItem>

              <DropdownItem tag={Link} to={`/products/edit/${cell.row.original.id}`}>
                <i className="ri-pencil-fill align-bottom me-2 text-muted"></i>{" "}
                {t("Edit")}
              </DropdownItem>

              <DropdownItem divider />
              <DropdownItem
                href="#"
                onClick={() => {
                  const productData = cell.row.original;
                  onClickDelete(productData);
                }}
              >
                <i className="ri-delete-bin-fill align-bottom me-2 text-muted"></i>{" "}
                {t("Delete")}
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        );
      },
    },
  ],
    [t, currentLang, categories]
  );

  document.title = t("Products") + " | Bozor Admin";

  return (
    <div className="page-content">
      <ToastContainer closeButton={false} limit={1} />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteProduct}
        onCloseClick={() => setDeleteModal(false)}
      />
      <DeleteModal
        show={deleteModalMulti}
        onDeleteClick={() => {
          deleteMultiple();
        }}
        onCloseClick={() => setDeleteModalMulti(false)}
      />

      <Container fluid>
        <Row>
          <Col xl={12}>
            <Card>
              <CardHeader className="border-0 mb-3">
                <Row className="align-items-center gy-3">
                  <div className="col-sm">
                    <h5 className="mb-0 fw-bold">{t("Products")}</h5>
                  </div>
                  <div className="col-sm-auto">
                    <div className="d-flex gap-1 flex-wrap align-items-center">
                      <div className="search-box me-2">
                        <input
                          type="text"
                          className="form-control"
                          placeholder={t("Search Products...")}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                      <Link
                        to="/products/create"
                        className="btn btn-success"
                      >
                        <i className="ri-add-line align-bottom me-1"></i> {t("Add Product")}
                      </Link>
                    </div>
                  </div>
                </Row>
              </CardHeader>

              <div className="card-body pt-0">
                {isLoading ? <Loader /> :
                  (productList && productList.length > 0 ? (
                    <TableContainer
                      columns={columns}
                      data={productList}
                      isGlobalFilter={false}
                      isAddUserList={false}
                      customPageSize={10}
                      pagination={{
                        setPage: setPage,
                        page: filters.page,
                        total: productsData?.pagination?.TotalPages || 1,
                      }}
                      divClass="table-responsive table-card mb-1"
                      tableClass="align-middle table-nowrap"
                      theadClass="table-light text-muted"
                    />
                  ) : (
                    <div className="py-4 text-center">
                      <div>
                        <lord-icon
                          src="https://cdn.lordicon.com/msoeawqm.json"
                          trigger="loop"
                          colors="primary:#405189,secondary:#0ab39c"
                          style={{ width: "72px", height: "72px" }}
                        ></lord-icon>
                      </div>
                      <div className="mt-4">
                        <h5>Sorry! No Result Found</h5>
                      </div>
                    </div>
                  ))}
              </div>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Products;
